import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from 'sonner';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import AccountNudge from './components/AccountNudge';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminPromotions from './pages/admin/AdminPromotions';

import ScrollToTop from './components/ScrollToTop';
import { PageTracker } from './hooks/useAnalytics';

// Pages
import HomePage from './pages/HomePage';
import CataloguePage from './pages/CataloguePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ConfirmationPage from './pages/ConfirmationPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PublicCataloguePage from './pages/PublicCataloguePage';
import AuthCallback from './pages/AuthCallback';
import AccountPage from './pages/AccountPage';
import FavoritesPage from './pages/FavoritesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Legal
import MentionsLegalesPage from './pages/legal/MentionsLegalesPage';
import CGVPage from './pages/legal/CGVPage';
import ConfidentialitePage from './pages/legal/ConfidentialitePage';
import CookiesPage from './pages/legal/CookiesPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductForm from './pages/admin/AdminProductForm';
import AdminOrders from './pages/admin/AdminOrders';
import AdminPromo from './pages/admin/AdminPromo';
import AdminPreparation from './pages/admin/AdminPreparation';
import AdminDeliveryNotes from './pages/admin/AdminDeliveryNotes';
import AdminUsers from './pages/admin/AdminUsers';
import AdminStats from './pages/admin/AdminStats';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminFeatured from './pages/admin/AdminFeatured';
import AdminBanner from './pages/admin/AdminBanner';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminDupes from './pages/admin/AdminDupes';
import AdminCredit from './pages/admin/AdminCredit';
import AdminPreorders from './pages/admin/AdminPreorders';
import PreorderPage from './pages/PreorderPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, staffOnly = false, adminOnly = false, perm = null }) => {
  const { user, loading, isAdmin, isStaff, hasPermission } = useAuth();
  const { isDark } = useTheme();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="animate-pulse text-[color:var(--color-primary)]">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/connexion" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (staffOnly && !isStaff) {
    return <Navigate to="/" replace />;
  }

  // Phase 38 — if a specific permission key is required, check it (admins
  // always pass). Falls back to the admin dashboard with a soft denial.
  if (perm && !hasPermission(perm)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Layout Component
const Layout = ({ children, hideFooter = false }) => {
  const { isDark } = useTheme();
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg)' }}>
      <Header />
      <main className="flex-1">{children}</main>
      {!hideFooter && <Footer />}
    </div>
  );
};

function AppRoutes() {
  const location = useLocation();
  // Retour Google (fragment #session_id=...) : traité avant toute autre route
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/catalogue" element={<Layout><CataloguePage /></Layout>} />
      <Route path="/precommande" element={<Layout><PreorderPage /></Layout>} />
      <Route path="/produit/:id" element={<Layout><ProductDetailPage /></Layout>} />
      <Route path="/panier" element={<Layout><CartPage /></Layout>} />
      <Route path="/checkout" element={<Layout><CheckoutPage /></Layout>} />
      <Route path="/confirmation" element={<Layout><ConfirmationPage /></Layout>} />
      <Route path="/favoris" element={<Layout><FavoritesPage /></Layout>} />

      {/* Legal Routes */}
      <Route path="/mentions-legales" element={<Layout><MentionsLegalesPage /></Layout>} />
      <Route path="/cgv" element={<Layout><CGVPage /></Layout>} />
      <Route path="/confidentialite" element={<Layout><ConfidentialitePage /></Layout>} />
      <Route path="/cookies" element={<Layout><CookiesPage /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/inscription" element={<RegisterPage />} />
      <Route path="/catalogue-pro/:token" element={<PublicCataloguePage />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
      
      {/* Protected Customer Routes */}
      <Route 
        path="/compte" 
        element={
          <ProtectedRoute>
            <Layout><AccountPage /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/orders" 
        element={
          <ProtectedRoute>
            <Layout><AccountPage /></Layout>
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes - Staff (admin + employee) */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute staffOnly>
            <Layout hideFooter><AdminDashboard /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/produits" 
        element={
          <ProtectedRoute staffOnly perm="products">
            <Layout hideFooter><AdminProducts /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/produits/nouveau" 
        element={
          <ProtectedRoute staffOnly perm="products">
            <Layout hideFooter><AdminProductForm /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/produits/:id" 
        element={
          <ProtectedRoute staffOnly perm="products">
            <Layout hideFooter><AdminProductForm /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/commandes" 
        element={
          <ProtectedRoute staffOnly perm="orders">
            <Layout hideFooter><AdminOrders /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/promo" 
        element={
          <ProtectedRoute staffOnly perm="promo">
            <Layout hideFooter><AdminPromo /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/preparation" 
        element={
          <ProtectedRoute staffOnly perm="pos">
            <Layout hideFooter><AdminPreparation /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/bons" 
        element={
          <ProtectedRoute staffOnly perm="delivery_notes">
            <Layout hideFooter><AdminDeliveryNotes /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coups-de-coeur" 
        element={
          <ProtectedRoute staffOnly perm="featured">
            <Layout hideFooter><AdminFeatured /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/banniere" 
        element={
          <ProtectedRoute staffOnly perm="featured">
            <Layout hideFooter><AdminBanner /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/devis" 
        element={
          <ProtectedRoute staffOnly perm="pos">
            <Layout hideFooter><AdminQuotes /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/promotions" 
        element={
          <ProtectedRoute staffOnly perm="promo">
            <Layout hideFooter><AdminPromotions /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/newsletter" 
        element={
          <ProtectedRoute staffOnly perm="featured">
            <Layout hideFooter><AdminNewsletter /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/precommandes" 
        element={
          <ProtectedRoute staffOnly perm="products">
            <Layout hideFooter><AdminPreorders /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/inspirations" 
        element={
          <ProtectedRoute staffOnly perm="products">
            <Layout hideFooter><AdminDupes /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/comptabilite" 
        element={
          <ProtectedRoute staffOnly>
            <Layout hideFooter><AdminCredit /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/utilisateurs" 
        element={
          <ProtectedRoute staffOnly perm="users">
            <Layout hideFooter><AdminUsers /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/statistiques" 
        element={
          <ProtectedRoute staffOnly perm="analytics">
            <Layout hideFooter><AdminStats /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute staffOnly perm="analytics">
            <Layout hideFooter><AdminAnalytics /></Layout>
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <FavoritesProvider>
              <ScrollToTop />
              <PageTracker />
              <AppRoutes />
              <CookieBanner />
              <AccountNudge />
              <Toaster 
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-heading)'
                  }
                }}
              />
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
